import isValid from 'date-fns/isValid';

import { isString, isStringOrUndefined } from '@r-client/shared/util/core';

interface IFilter {
  (v: string): boolean;
}

export const isPhone = (value: string) => {
  const filtered = `${value}`.replace(/[\s-())]+/g, '');
  return /^\+?[\d]{8,}$/g.test(filtered);
};

export const isValidEmail = (email: string) => {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return pattern.test(email);
};

export const phoneValidation =
  (message = 'Please enter a valid phone number') =>
  (val: unknown) =>
    (typeof val === 'string' && isPhone(val)) || !val ? undefined : message;

export const emailValidation =
  (message = 'Please enter a valid email') =>
  (val: unknown) =>
    typeof val === 'string' && isValidEmail(val) ? undefined : message;



export const isValidUrl = (url: string) => {
  const pattern =
    /^(https?|ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i;
  return pattern.test(url);
};

export const urlValidation =
  (message = 'Please enter a valid URL') =>
  (val: unknown) =>
    (typeof val === 'string' && isValidUrl(val)) || !val ? undefined : message;

export const isUrlForDomains = (
  domains: { baseDomain: string; flexSubdomainCount?: number }[],
  url: string
) => {
  const domainsForRegex = domains
    .map(
      ({ baseDomain, flexSubdomainCount }) =>
        `[^.]+\\.`.repeat(flexSubdomainCount ?? 0) +
        baseDomain.replace('.', '\\.')
    )
    .join('|');
  const pattern = new RegExp(`^(https?://)?(www\\.)?(${domainsForRegex})/`);
  return pattern.test(url);
};

export const isValidLinkedinUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'linkedin.com' }], url)
  );
};

export const isValidFacebookUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'facebook.com' }], url)
  );
};

export const isValidAngelListUrl = (url: string) => {
  return isValidUrl(url) && isUrlForDomains([{ baseDomain: 'angel.co' }], url);
};

export const isValidDiscordUrl = (url: string) => {
  return (
    isValidUrl(url) &&
    isUrlForDomains(
      [
        { baseDomain: 'discord.gg' },
        { baseDomain: 'discord.io' },
        { baseDomain: 'discord.me' },
        { baseDomain: 'discord.li' },
        { baseDomain: 'discord.com' },
        { baseDomain: 'discordapp.com' },
      ],
      url
    )
  );
};

export const isValidCrunchbaseUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'crunchbase.com' }], url)
  );
};

export const isValidInstagramUrl = (url: string) => {
  return (
    isValidUrl(url) &&
    isUrlForDomains(
      [
        { baseDomain: 'instagram.com' },
        { baseDomain: 'instagr.am' },
        { baseDomain: 'instagr.com' },
      ],
      url
    )
  );
};

export const isValidYoutubeUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'youtube.com' }], url)
  );
};

export const isValidMediumUrl = (url: string) => {
  return (
    isValidUrl(url) &&
    isUrlForDomains([{ baseDomain: 'medium.com', flexSubdomainCount: 1 }], url)
  );
};

export const isValidPinterestUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'pinterest.com' }], url)
  );
};

export const isValidTikTokUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'tiktok.com' }], url)
  );
};

export const isValidTwitterUrl = (url: string) => {
  return (
    isValidUrl(url) && isUrlForDomains([{ baseDomain: 'twitter.com' }], url)
  );
};



export const repeated =
  (minTimes: number, test: IFilter = () => true) =>
  (input: string) => {
    const { maxCount } = input.split('').reduce(
      (acc, letter) => {
        const count = test(letter) && acc.prev === letter ? acc.count + 1 : 1;
        return {
          prev: letter,
          count,
          maxCount: Math.max(acc.maxCount, count),
        };
      },
      { count: 1, maxCount: 1 } as {
        prev?: string;
        count: number;
        maxCount: number;
      }
    );
    return maxCount >= minTimes;
  };

export const format = (txt: string, ...args: unknown[]) =>
  txt.replace(/\$([0-9])/g, (_, val) => args[+val] as string);


export const required =
  (message = "Can't be blank") =>
  (val: unknown) =>
    val ? undefined : message;

export const moreEq =
  (len: number, message = `Minimum $0`) =>
  (val: unknown) =>
    val && `${val}`.length >= len ? undefined : format(message, len);

export const more =
  (len: number, message = `Must be more than $0`) =>
  (val: unknown) =>
    val && `${val}`.length > len ? undefined : format(message, len);


export const lessEq =
  (len: number, message = `Maximum $0`) =>
  (val: unknown) =>
    val && `${val}`.length <= len ? undefined : format(message, len);


export const regExp = (rx: RegExp, message: string) => (val: unknown) =>
  isString(val) && rx.test(val) ? undefined : message;

export const condSkip = (
  test: (val: unknown) => boolean,
  fn: (val: unknown) => string | undefined
): ((val: unknown) => string | undefined) => {
  return (val: unknown): undefined | string => {
    if (test(val)) {
      return fn(val);
    }
    return undefined;
  };
};

export const compose =
  (...fns: Array<(val: unknown) => string | undefined>) =>
  (val: unknown) => {
    for (const fn of fns) {
      const res = fn?.(val);
      if (res) {
        return res;
      }
    }
    return;
  };



// latin and numbers
export function isOnlyLatin(
  value?: string,
  exceptions: string | string[] = ''
): boolean {
  return (value
    ?.replace(/\s/g, '')
    .split('')
    .every((letter) => {
      const exceptionsResult = Array.prototype.join.call(exceptions, '');

      // See https://linear.app/republic/issue/REA-1158/validation-allow-to-use-symbols-in-the-name-field
      if (`-—'${exceptionsResult}`.indexOf(letter) >= 0) {
        return true;
      }

      if (/\W/.test(letter[0])) {
        return false;
      }

      return true;
    }) ?? true);
}

export const isLatinChars = (val?: string) =>
  isString(val) ? /^[a-z ,.'-]*$/i.test(val) : false;

export const isLatinCharsValidation =
  (message = 'Please use latin characters') =>
  (val: unknown) =>
    isStringOrUndefined(val) && isLatinChars(val) ? undefined : message;

export const isOnlyLatinValidation =
  (message = 'Please use latin characters', exceptions?: string | string[]) =>
  (val: unknown) =>
    isStringOrUndefined(val) && isOnlyLatin(val, exceptions)
      ? undefined
      : message;



export const notDigits =
  (message = "Please don't use only digits") =>
  (val: unknown) =>
    isString(val) && /^[0-9 ]+$/.test(val) ? message : undefined;







// Ref: https://date-fns.org/v2.21.3/docs/isValid
export const isValidDate =
  (message = 'Invalid date') =>
  (val: unknown) =>
    (typeof val === 'number' || typeof val === 'string') &&
    isValid(new Date(val))
      ? undefined
      : message;

export const requiredAndPositiveAmount = () =>
  compose(required(undefined), more(0, "Can't be $$0"));



export const isValidPassword = (password: string) => {
  return /^.{8,72}$/.test(password);
};

export const passwordValidation =
  (message = 'Too short (minimum is 8 characters)') =>
  (val: unknown) =>
    typeof val === 'string' && isValidPassword(val) ? undefined : message;

export const confirmPassword =
  (password: string, message = 'Passwords must match') =>
  (val: unknown) =>
    val === password ? undefined : message;

export const minimumLength = (length: number) => (val: unknown) =>
  ((typeof val === 'string' && val?.length) || 0) < length
    ? `${length} character minimum`
    : undefined;

export const maximumLength = (length: number) => (val: unknown) =>
  ((typeof val === 'string' && val?.length) || Infinity) > length
    ? `${length} character maximum`
    : undefined;

export const exactLength = (length: number) => (val: unknown) =>
  ((typeof val === 'string' && val?.length) || -1) !== length
    ? `Must be ${length} characters long`
    : undefined;

export const containsLetter = () => (val: unknown) =>
  typeof val === 'string' && !val.match(/[a-z]/i)
    ? `Must contain one letter`
    : undefined;

export const containsDigit = () => (val: unknown) =>
  typeof val === 'string' && !val.match(/[0-9]/)
    ? `Must contain one number`
    : undefined;

export const isInteger = () => (val: unknown) =>
  typeof val === 'string' && !val.match(/^[0-9]*$/)
    ? `Must be only digits (0-9)`
    : undefined;

export const containsAtLeastOneOf =
  (charList: Array<string>) => (val: unknown) =>
    typeof val === 'string' && !val.match(new RegExp(`[${charList.join('')}]`))
      ? `Must contain at least one of (${charList})`
      : undefined;
